import { AppointmentService } from '../../api/services/AppointmentService';
import { Routemize_DTOs_Availability_TeamMemberAvailabilityStatus as ApiStatus } from '../../api/models/Routemize_DTOs_Availability_TeamMemberAvailabilityStatus';
import type { Routemize_DTOs_Availability_TeamMemberAvailabilityDto } from '../../api/models/Routemize_DTOs_Availability_TeamMemberAvailabilityDto';
import type { Routemize_DTOs_Availability_TeamMemberAvailabilityConflictDto } from '../../api/models/Routemize_DTOs_Availability_TeamMemberAvailabilityConflictDto';

/**
 * View-model status for the exact-time picker's "Team member availability" cards.
 * Mirrors the backend enum values (Available / Unavailable / NotWorking) as a string union
 * the presentational component can switch on without importing the generated enum.
 */
export type TeamMemberAvailabilityStatus = 'Available' | 'Unavailable' | 'NotWorking';

/** A busy slot overlapping the chosen window — why a member is Unavailable. */
export interface TeamMemberAvailabilityConflictVM {
  /** "Appointment" or "ExternalCalendar" (best-effort label when busy slots were merged). */
  source: string;
  title: string | null;
  startUtc: string;
  endUtc: string;
}

export interface TeamMemberAvailabilityVM {
  userId: string;
  name: string;
  status: TeamMemberAvailabilityStatus;
  /** Appointments already scheduled for this member on the chosen day (Cancelled/NoShow excluded). */
  appointmentCount: number;
  /** The "Y" in "X / Y" — the member's per-day cap for the booking's Event Type; null when uncapped. */
  dailyCapacity: number | null;
  /** 1-based Auto-Assign rank when the backend populates it; null otherwise. */
  autoAssignRank: number | null;
  /** The busy slots overlapping the window, whatever the status (a NotWorking member can also have them). */
  conflicts: TeamMemberAvailabilityConflictVM[];
  /** True when appointmentCount has reached dailyCapacity — the booking/update cap guard would reject. */
  isDailyCapReached: boolean;
}

export interface FetchTeamMemberAvailabilityParams {
  userIds: string[];
  startUtc: Date;
  endUtc: Date;
  bookingFormId?: string;
  /**
   * When editing/rescheduling, the appointment being moved. It is excluded from the busy-times conflict
   * check and the per-day count so overlapping its own current slot doesn't mark the assignee busy.
   */
  excludeAppointmentId?: string;
  latitude?: number;
  longitude?: number;
}

const mapStatus = (status?: ApiStatus): TeamMemberAvailabilityStatus => {
  switch (status) {
    case ApiStatus.UNAVAILABLE:
      return 'Unavailable';
    case ApiStatus.NOT_WORKING:
      return 'NotWorking';
    case ApiStatus.AVAILABLE:
    default:
      return 'Available';
  }
};

const mapConflictToVM = (
  dto: Routemize_DTOs_Availability_TeamMemberAvailabilityConflictDto,
): TeamMemberAvailabilityConflictVM => ({
  source: dto.source ?? '',
  title: dto.title ?? null,
  startUtc: dto.startUtc ?? '',
  endUtc: dto.endUtc ?? '',
});

const mapToVM = (dto: Routemize_DTOs_Availability_TeamMemberAvailabilityDto): TeamMemberAvailabilityVM => ({
  userId: dto.userId ?? '',
  name: dto.userName ?? 'A team member',
  status: mapStatus(dto.status),
  appointmentCount: dto.appointmentCount ?? 0,
  dailyCapacity: dto.dailyCapacity ?? null,
  autoAssignRank: dto.autoAssignRank ?? null,
  conflicts: (dto.conflicts ?? []).map(mapConflictToVM),
  isDailyCapReached: dto.isDailyCapReached ?? false,
});

/**
 * Wraps the read-only per-member availability endpoint the exact-time picker calls when the operator
 * has picked a window on a multi-salesperson smart-assignment form. Maps the DTO into a small view model.
 */
export class TeamMemberAvailabilityManager {
  async fetch(params: FetchTeamMemberAvailabilityParams): Promise<TeamMemberAvailabilityVM[]> {
    const result = await AppointmentService.postApiAppAppointmentGetTeamMemberAvailability({
      userIds: params.userIds,
      startUtc: params.startUtc.toISOString(),
      endUtc: params.endUtc.toISOString(),
      bookingFormId: params.bookingFormId,
      excludeAppointmentId: params.excludeAppointmentId,
      latitude: params.latitude,
      longitude: params.longitude,
    });
    return (result ?? []).map(mapToVM);
  }
}

export const teamMemberAvailabilityManager = new TeamMemberAvailabilityManager();
